import * as React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Paper from "@mui/material/Paper";
import { visuallyHidden } from "@mui/utils";
import { tableHeadStyle, tableRowStyle, tableContainerStyle, tableCellStyle } from "./Styles";

function createData(
  id,
  type,
  discipline,
  lastName,
  firstName,
  hireDate,
  quarterlyTraining,
  credentialStatus
) {
  return {
    id,
    type,
    discipline,
    lastName,
    firstName,
    hireDate,
    quarterlyTraining,
    credentialStatus,
  };
}

const rows = [
  createData(
    1,
    "Active",
    "PT",
    "Cameron",
    "Williamson",
    "19-02-2023",
    "Due in 5 days",
    "Attention"
  ),
  createData(
    2,
    "Active",
    "PT",
    "Cameron",
    "Williamson",
    "19-02-2023",
    "Due in 5 days",
    "Attention"
  ),

  createData(
    3,
    "Active",
    "PT",
    "Cameron",
    "Williamson",
    "19-02-2023",
    "Due in 5 days",
    "Attention"
  ),
  createData(
    4,
    "Active",
    "PT",
    "Cameron",
    "Williamson",
    "19-02-2023",
    "Due in 5 days",
    "Attention"
  ),
];

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: "id",
    numeric: false,
    disablePadding: true,
    label: "#",
  },
  {
    id: "type",
    numeric: true,
    disablePadding: false,
    label: "Type",
  },
  {
    id: "discipline",
    numeric: true,
    disablePadding: false,
    label: "Discipline",
  },
  {
    id: "lastName",
    numeric: true,
    disablePadding: false,
    label: "Last Name",
  },
  {
    id: "firstName",
    numeric: true,
    disablePadding: false,
    label: "First Name",
  },

  {
    id: "hireDate",
    numeric: true,
    disablePadding: false,
    label: "Hire Date",
  },
  {
    id: "quarterlyTraining",
    numeric: true,
    disablePadding: false,
    label: "Quarterly Training",
  },
  {
    id: "credentialStatus",
    numeric: true,
    disablePadding: false,
    label: "Status",
  },
];


function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow >
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align="start"
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
            sx={tableHeadStyle}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

export default function HrQTrainingTable() {
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("calories");
  const [selected, setSelected] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);

  const handleRequestSort = (_event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = rows.map((n) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const visibleRows = React.useMemo(
    () =>
      stableSort(rows, getComparator(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),
    [order, orderBy, page, rowsPerPage]
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={tableContainerStyle}>
        <TableContainer>
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size="medium"
          >
            <EnhancedTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
            />
            <TableBody>
              {visibleRows.map((row, _index) => {
                return (
                  <TableRow sx={tableRowStyle} tabIndex={-1} key={row.id}>
                    <TableCell sx={tableCellStyle} align="start">{row.id}</TableCell>
                    <TableCell sx={tableCellStyle} align="start" className="!text-[#32D583]">
                      {row.type}
                    </TableCell>
                    <TableCell sx={tableCellStyle} align="start">{row.discipline}</TableCell>
                    <TableCell sx={tableCellStyle} align="start">{row.lastName}</TableCell>
                    <TableCell sx={tableCellStyle} align="start">{row.firstName}</TableCell>
                    <TableCell sx={tableCellStyle} align="start">{row.hireDate}</TableCell>
                    <TableCell sx={tableCellStyle} align="start">
                      {row.quarterlyTraining}
                    </TableCell>
                    <TableCell
                      sx={tableCellStyle} 
                      align="start"
                      className="!text-[#F4B40E] !font-bold"
                    >
                      {row.credentialStatus}
                    </TableCell>
                  </TableRow>
                );
              })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: 53 * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
}
